"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Strategist() {
  const {
    ref: headingRef,
    isVisible: headingVisible
  } = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.2
  });
  const {
    ref: p1Ref,
    isVisible: p1Visible
  } = useRevealOnScroll<HTMLParagraphElement>({
    threshold: 0.2
  });
  const {
    ref: p2Ref,
    isVisible: p2Visible
  } = useRevealOnScroll<HTMLParagraphElement>({
    threshold: 0.2
  });
  const {
    ref: linkRef,
    isVisible: linkVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: metaRef,
    isVisible: metaVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const founders = [{
    name: "Yasindhu Ravith",
    role: "Digital Marketer & Strategist"
  }, {
    name: "Creative Lead",
    role: "Content & Web Specialist"
  }, {
    name: "BASED IN SRI LANKA",
    role: ""
  }];
  return <section data-section-id="10132" id="the-strategist-behind-the-screen" className="bg-background text-foreground py-16 md:py-24">
      <div className="max-w-[1320px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-start">
          {}
          <div className="lg:col-span-7 order-2 lg:order-1">
            <h2 ref={headingRef} className="font-default font-bold text-foreground leading-[1.02] tracking-[-0.018em]" style={{
            fontSize: "clamp(2rem, 4.5vw, 4rem)",
            opacity: headingVisible ? 1 : 0,
            transform: headingVisible ? "translateY(0)" : "translateY(16px)",
            transition: "opacity 700ms cubic-bezier(0.2, 0.7, 0.2, 1), transform 700ms cubic-bezier(0.2, 0.7, 0.2, 1)"
          }}>
              A digital architect with a <em className="italic font-default bg-gradient-to-r from-purple-500 to-blue-500 bg-clip-text text-transparent">measurable</em> impact.
            </h2>

            <div className="mt-10 md:mt-12 space-y-6 md:space-y-7 max-w-2xl">
              <p ref={p1Ref} className="font-default text-muted-foreground leading-[1.55]" style={{
              fontSize: "clamp(1.125rem, 1.4vw, 1.375rem)",
              opacity: p1Visible ? 1 : 0,
              transform: p1Visible ? "translateY(0)" : "translateY(16px)",
              transition: "opacity 700ms cubic-bezier(0.2, 0.7, 0.2, 1) 80ms, transform 700ms cubic-bezier(0.2, 0.7, 0.2, 1) 80ms"
            }}>
                My journey began at 12, turning a small YouTube channel into a platform for millions. I believe that digital marketing is not just about reach, but about the precision of the strategy behind every click. I started this portfolio to document the growth, the data, and the creative experiments that define my work.
              </p>

              <p ref={p2Ref} className="font-default text-muted-foreground leading-[1.55]" style={{
              fontSize: "clamp(1.125rem, 1.4vw, 1.375rem)",
              opacity: p2Visible ? 1 : 0,
              transform: p2Visible ? "translateY(0)" : "translateY(16px)",
              transition: "opacity 700ms cubic-bezier(0.2, 0.7, 0.2, 1) 160ms, transform 700ms cubic-bezier(0.2, 0.7, 0.2, 1) 160ms"
            }}>
                Over the last few years, I have helped brands scale through content strategy, web development, and performance tracking. Every project is an opportunity to refine the process and deliver results that matter. My ledger is open, and every metric is a testament to the work we do together.
              </p>

              <div ref={linkRef} style={{
              opacity: linkVisible ? 1 : 0,
              transform: linkVisible ? "translateY(0)" : "translateY(16px)",
              transition: "opacity 700ms cubic-bezier(0.2, 0.7, 0.2, 1) 240ms, transform 700ms cubic-bezier(0.2, 0.7, 0.2, 1) 240ms"
            }}>
                <Link to="/PortfolioPage" className="group inline-flex items-center gap-2 font-default text-primary underline decoration-primary underline-offset-[6px] decoration-1 hover:decoration-2 transition-all duration-150 text-base">
                  <span>View my full portfolio</span>
                  <ArrowRight className="size-4 transition-transform duration-200 ease-out group-hover:translate-x-1" />
                </Link>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-5 order-1 lg:order-2">
            <div ref={imageRef} className="group overflow-hidden rounded-2xl shadow-lg" style={{
            opacity: imageVisible ? 1 : 0,
            transition: "opacity 900ms cubic-bezier(0.2, 0.7, 0.2, 1)"
          }}>
              <div className="relative w-full overflow-hidden" style={{
              aspectRatio: "4 / 5"
            }}>
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1712154/img/yasindhu_ravith_profile.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1712154/img/yasindhu_ravith_profile.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1712154/img/yasindhu_ravith_profile.webp-wvc-sizes" alt="Yasindhu Ravith, Digital Marketer and Content Strategist" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[600ms] ease-out group-hover:scale-[1.03]" src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1712154/img/file_000000008f6c72069cc79786741fd6c2.png" src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1712154/img/file_000000008f6c72069cc79786741fd6c2.png" src="https://yasindhuravith.com/wp-content/uploads/2026/06/file_000000008f6c72069cc79786741fd6c2.png" />
              </div>
            </div>

            <div ref={metaRef} className="mt-6 space-y-1.5" style={{
            opacity: metaVisible ? 1 : 0,
            transform: metaVisible ? "translateY(0)" : "translateY(16px)",
            transition: "opacity 700ms cubic-bezier(0.2, 0.7, 0.2, 1) 200ms, transform 700ms cubic-bezier(0.2, 0.7, 0.2, 1) 200ms"
          }}>
              {founders.map((f, i) => <p key={i} data-index={i} className="font-default text-muted-foreground uppercase" style={{
              fontSize: "0.75rem",
              letterSpacing: "0.04em",
              lineHeight: 1.5
            }}>
                  {f.role ? `${f.name} · ${f.role}` : f.name}
                </p>)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}